<script lang="ts">
	import type { SearchCarResult } from '$lib/components/search/cars/types';
	import { page } from '$app/state';
	import { GetDefaultCarOptionsStore, UpdateRegisteredCarStore } from '$houdini';

	import Header from '$lib/components/header/Header.svelte';
	import CarForm from '$lib/components/itinerary/options/cars/CarForm/CarForm.svelte';
	import { addNotification } from '$lib/components/notifications/notifications';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { trackSaveCar } from '$lib/services/tracking/googleAnalytics/car';
	import {
		getSearchParamsStore,
		goBackWithFallback,
		gotoWithParams,
	} from '$lib/services/urls/searchParams';
	import * as m from '$paraglide/messages';
	import { onMount } from 'svelte';

	const searchParams = getSearchParamsStore();

	let selectedCar: SearchCarResult | null = $state(null);
	let userCarId: string | undefined = $state();

	const isUserLoggedIn = $derived(!!page.data.userSession);
	let initialStateLoading = $derived(!!isUserLoggedIn);

	let saveCarError = false;
	let saveCarQueryStore: UpdateRegisteredCarStore;
	let getDefaultCarOptionsQueryStore: GetDefaultCarOptionsStore;

	function saveCar(carId: string | undefined) {
		if (!saveCarQueryStore) {
			saveCarQueryStore = new UpdateRegisteredCarStore();
		}

		if (!carId) {
			return;
		}

		saveCarError = false;
		saveCarQueryStore
			.mutate({
				input: {
					carId,
				},
			})
			.then(() => {
				addNotification(m.register_car__register_success());
				gotoWithParams('/routes', { ...$searchParams });
			})
			.catch(() => {
				saveCarError = true;
				addNotification(m.register_car__register_error());
			});
	}

	function fetchDefaultCarOptions(isUserLoggedIn: boolean) {
		if (!isUserLoggedIn) {
			return;
		}

		if (!getDefaultCarOptionsQueryStore) {
			getDefaultCarOptionsQueryStore = new GetDefaultCarOptionsStore();
		}

		getDefaultCarOptionsQueryStore
			.fetch({
				policy: 'NetworkOnly',
			})
			.then((result) => {
				const defaultCarOptionsResult = result.data?.getDefaultCarOptions;
				if (!defaultCarOptionsResult?.car) {
					return;
				}
				const { car, userCarId: carId } = defaultCarOptionsResult;

				selectedCar = car;
				userCarId = carId ?? undefined;
			})
			.finally(() => {
				initialStateLoading = false;
			});
	}

	onMount(() => {
		fetchDefaultCarOptions(isUserLoggedIn);
	});

	function handleBack() {
		track({
			action: 'back',
			payload: {
				action: 'click',
			},
		});
		goBackWithFallback('/map', { ...$searchParams });
	}
</script>

<div class="fixed md:relative top-0 h-full w-full pt-1 md:pt-2 bg-neutral-weakest overflow-y-auto">
	<Header
		hasRoleBanner
		variant="no-logo"
		leftButton={{ icon: 'west', text: m.action__back() }}
		rightButton={{ href: '/', label: m.action__back_home() }}
		onLeftButtonClick={handleBack}
	/>
	<CarForm
		bind:selectedCar
		isAuthenticated={isUserLoggedIn}
		saveCarLoading={$saveCarQueryStore?.fetching && !saveCarError}
		{initialStateLoading}
		onSaveCar={(carId) => {
			trackSaveCar(carId, selectedCar);
			saveCar(carId);
		}}
		{userCarId}
		class="px-2 mt-4"
	/>
</div>
